
// ... (imports remain the same)
import React, { useState, useEffect, useRef } from 'react';
import { Home, ChevronRight, Search, Filter, Plus, X, Edit, Trash2, Database, Download, Upload, Sheet, FileText, Folder as FolderIcon, AlertTriangle, CheckCircle, XCircle, Sparkles, Loader2, Camera, Keyboard, Layers, Globe, ArrowLeft, ArrowRight, GripHorizontal, FileEdit, BookOpen, FolderInput, CheckSquare, Square } from 'lucide-react';
import { GoogleGenAI } from "@google/genai";
import { Question, Folder } from '../data/types';
import { getTags, downloadFile, questionsToCSV, parseCSV, parseCSVLine } from '../utils';
import { Modal } from './Common';

const CUSTOM_API_KEY_STORAGE = 'math_quiz_custom_api_key';
const SYSTEM_ROOT_ID = 'SYSTEM_ROOT_VIRTUAL_ID';

export const AdminPanel = ({ questions, folders, setQuestions, setFolders, rootId }: any) => {
    // currentFolderId can be SYSTEM_ROOT_ID to manage top-level grades
    const [currentFolderId, setCurrentFolderId] = useState<string | null>(rootId);
    
    // When rootId changes (grade switch from sidebar), reset current view to that grade root
    useEffect(() => {
        setCurrentFolderId(rootId);
    }, [rootId]);

    const [editingId, setEditingId] = useState<number | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    // ... (rest of state)
    const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
    const [tagInput, setTagInput] = useState('');
    const [searchText, setSearchText] = useState('');
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    
    // View Mode State
    const [showRecursive, setShowRecursive] = useState(false);
    const [isReordering, setIsReordering] = useState(false);
    
    // FAB State
    const [isFabOpen, setIsFabOpen] = useState(false);
    
    // CSV Quick Add State
    const [isCsvQuickAddOpen, setIsCsvQuickAddOpen] = useState(false);
    const [quickCsvContent, setQuickCsvContent] = useState("");

    // Image Recognition State
    const [isRecognizingImage, setIsRecognizingImage] = useState(false);
    const imageInputRef = useRef<HTMLInputElement>(null);
    
    // Export Modal State
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [exportFileName, setExportFileName] = useState("");

    // Note Editing State
    const [isNoteEditorOpen, setIsNoteEditorOpen] = useState(false);
    const [currentNoteFolder, setCurrentNoteFolder] = useState<Folder | null>(null);
    const [noteContent, setNoteContent] = useState("");

    // Move Question State (Bulk)
    const [selectedQuestionIds, setSelectedQuestionIds] = useState<Set<number>>(new Set());
    const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
    const [moveTargetFolderId, setMoveTargetFolderId] = useState<string>("");

    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean;
        type: 'delete-folder' | 'delete-question' | 'create-folder' | 'info' | 'error' | 'rename-folder' | null;
        targetId?: string | number;
        title: string;
        content?: React.ReactNode;
        message?: string;
        initialValue?: string;
    }>({ isOpen: false, type: null, title: '' });

    const [folderNameInput, setFolderNameInput] = useState("");
    
    const jsonInputRef = useRef<HTMLInputElement>(null);
    const csvInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setCategoryFilter('ALL');
        setSearchText('');
        setShowRecursive(false); // Reset recursive view when changing folders
        setIsReordering(false); // Reset reorder mode
        setSelectedQuestionIds(new Set()); // Reset selection
    }, [currentFolderId]);

    // Check if we are at the System Root level
    const isSystemRoot = currentFolderId === SYSTEM_ROOT_ID;
    // Check if we are at the Active Grade Root level
    const isActiveGradeRoot = currentFolderId === rootId;

    // Get subfolders based on current view
    const currentSubFolders = isSystemRoot
        ? folders.filter((f: Folder) => f.parentId === null) // All Grades
        : folders.filter((f: Folder) => (f.parentId || null) === (currentFolderId || null));
    
    // Helper to get recursive questions
    const getRecursiveQuestions = (fid: string | null): Question[] => {
        let result = questions.filter((q: Question) => (q.folderId || null) === (fid || null));
        const subFolders = folders.filter((f: Folder) => (f.parentId || null) === (fid || null));
        subFolders.forEach((sub: Folder) => {
            result = [...result, ...getRecursiveQuestions(sub.id)];
        });
        return result;
    };

    // Questions only display when NOT in System Root
    const currentFolderQuestions = (isSystemRoot) 
        ? [] 
        : (showRecursive 
            ? getRecursiveQuestions(currentFolderId)
            : questions.filter((q: Question) => (q.folderId || null) === (currentFolderId || null)));

    const filteredQuestions = currentFolderQuestions.filter((q: Question) => {
        const matchesCategory = categoryFilter === 'ALL' || getTags(q.category).includes(categoryFilter);
        const matchesSearch = searchText === '' || 
            q.text.toLowerCase().includes(searchText.toLowerCase()) || 
            q.explanation.toLowerCase().includes(searchText.toLowerCase()) ||
            q.id.toString().includes(searchText);
        
        return matchesCategory && matchesSearch;
    });

    // Dynamic Breadcrumbs Logic
    const getFullPath = (fid: string | null): Folder[] => {
        if (!fid || fid === SYSTEM_ROOT_ID) return [];
        const path: Folder[] = [];
        let currId = fid;
        while(currId) {
            const f = folders.find((fd: Folder) => fd.id === currId);
            if (f) {
                path.unshift(f);
                currId = f.parentId;
            } else {
                break;
            }
        }
        return path;
    };

    // Helper to get full path string for dropdown
    const getFolderPathString = (folder: Folder): string => {
        const path = getFullPath(folder.id);
        return path.map(p => p.name).join(' / ');
    };

    // --- Scope Detection Logic for Move ---
    const isDescendant = (childId: string, ancestorId: string): boolean => {
        if (ancestorId === SYSTEM_ROOT_ID) return true;
        if (childId === ancestorId) return true;
        
        let curr = folders.find((f: Folder) => f.id === childId);
        while (curr) {
            if (curr.id === ancestorId) return true;
            if (!curr.parentId) return false;
            curr = folders.find((f: Folder) => f.id === curr.parentId);
        }
        return false;
    };

    const getSubjectScopeId = (): string => {
        if (isSystemRoot) return SYSTEM_ROOT_ID;
        // If undefined, fallback to rootId (Active Grade)
        if (!currentFolderId) return rootId;

        let curr = folders.find((f: Folder) => f.id === currentFolderId);
        if (!curr) return rootId;

        // If current folder is a Root (no parent), return itself
        if (!curr.parentId) return curr.id;

        // Traverse up until we find the node directly below a Root (The Subject Level)
        // This ensures we stay within the Subject branch regardless of the active rootId
        while (curr && curr.parentId) {
            const parent = folders.find((f: Folder) => f.id === curr!.parentId);
            if (!parent) return rootId; // Parent not found, fallback
            
            // If parent is a root, then 'curr' is the Subject
            if (parent.parentId === null) {
                return curr.id;
            }
            curr = parent;
        }
        return rootId;
    };

    const targetScopeId = getSubjectScopeId();
    const scopeFolder = folders.find((f: Folder) => f.id === targetScopeId);
    const scopeName = targetScopeId === SYSTEM_ROOT_ID ? "全系統" : 
                      targetScopeId === rootId ? "本年級/題庫" : 
                      (scopeFolder ? scopeFolder.name : "本科目");

    const availableTargetFolders = folders
        .filter((f: Folder) => {
            if (f.id === currentFolderId) return false;
            if (f.parentId === null && !isSystemRoot) return false; // Hide roots if not in system mode
            return isDescendant(f.id, targetScopeId);
        })
        .sort((a: Folder, b: Folder) => getFolderPathString(a).localeCompare(getFolderPathString(b)));
    // --------------------------------------

    const fullPath = getFullPath(currentFolderId);
    const rootFolder = folders.find((f: Folder) => f.id === rootId);
    
    // Determine the "Home" (Root) of the breadcrumb trail
    let breadcrumbRootId = rootId;
    let breadcrumbRootName = rootFolder ? rootFolder.name : '未知年級';
    let displayBreadcrumbs: Folder[] = [];

    // Check if the current path actually belongs to the active root (activeGradeId)
    const activeRootInPathIndex = fullPath.findIndex(f => f.id === rootId);
    
    if (activeRootInPathIndex !== -1) {
        // Yes, we are inside the active root
        displayBreadcrumbs = fullPath.slice(activeRootInPathIndex + 1);
    } else {
        // No, we are somewhere else (e.g. via System Root navigation)
        if (fullPath.length > 0) {
            // Use the top-most folder of the current path as the "Root"
            breadcrumbRootId = fullPath[0].id;
            breadcrumbRootName = fullPath[0].name;
            displayBreadcrumbs = fullPath.slice(1);
        } else {
            // We are likely at System Root, or fullPath is empty
            displayBreadcrumbs = []; 
        }
    }

    const emptyForm = {
        category: '',
        text: '',
        options: ['', '', '', ''],
        correctAnswerIndex: 0,
        explanation: ''
    };
    const [formData, setFormData] = useState(emptyForm);

    // --- Reordering Logic ---
    const moveFolder = (folderId: string, direction: 'left' | 'right') => {
        const siblings = currentSubFolders;
        const currentIndex = siblings.findIndex((f: Folder) => f.id === folderId);
        
        if (currentIndex === -1) return;
        
        const targetIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
        
        // Check bounds
        if (targetIndex < 0 || targetIndex >= siblings.length) return;
        
        const folderA = siblings[currentIndex];
        const folderB = siblings[targetIndex];
        
        // Find indexes in the global folders array
        const newFolders = [...folders];
        const indexA = newFolders.findIndex((f: Folder) => f.id === folderA.id);
        const indexB = newFolders.findIndex((f: Folder) => f.id === folderB.id);
        
        // Swap
        [newFolders[indexA], newFolders[indexB]] = [newFolders[indexB], newFolders[indexA]];
        
        setFolders(newFolders);
    };
    // ------------------------

    // ... (rest of the file remains unchanged, just need to return the rest of the component)
    // To minimize output, I will rely on the fact that I'm only updating the logic block and returning the whole file content is required by the prompt rules.
    
    const startEdit = (q: Question) => {
        setFormData({
            category: q.category,
            text: q.text,
            options: [...q.options],
            correctAnswerIndex: q.correctAnswerIndex,
            explanation: q.explanation
        });
        setTagInput('');
        setEditingId(q.id);
        setIsAdding(false);
    };

    const startAdd = () => {
        setFormData(emptyForm);
        setTagInput('');
        setEditingId(null);
        setIsAdding(true);
    };

    const saveForm = () => {
        if (!formData.text || !formData.category) {
            setModalConfig({ isOpen: true, type: 'error', title: '錯誤', message: '請填寫題目與至少一個分類標籤' });
            return;
        }

        if (isAdding) {
            const newId = questions.length > 0 ? Math.max(...questions.map((q: Question) => q.id)) + 1 : 1;
            const newQ: Question = {
                id: newId,
                folderId: currentFolderId === SYSTEM_ROOT_ID ? null : currentFolderId, 
                ...formData
            };
            setQuestions([...questions, newQ]);
            setIsAdding(false);
        } else if (editingId !== null) {
            const updatedQ: Question = {
                id: editingId,
                folderId: currentFolderId === SYSTEM_ROOT_ID ? null : currentFolderId,
                ...formData,
                svg: questions.find((q: Question) => q.id === editingId)?.svg
            };
            
            const originalQ = questions.find((q: Question) => q.id === editingId);
            if (originalQ) {
                 updatedQ.folderId = originalQ.folderId; 
            }
            
            setQuestions(questions.map((q: Question) => q.id === updatedQ.id ? updatedQ : q));
            setEditingId(null);
        }
    };

    const getApiKey = () => {
        return localStorage.getItem(CUSTOM_API_KEY_STORAGE) || process.env.API_KEY || '';
    };

    // --- Note Editing Logic ---
    const openNoteEditor = (folder: Folder) => {
        setCurrentNoteFolder(folder);
        setNoteContent(folder.noteContent || "");
        setIsNoteEditorOpen(true);
    };

    const saveNote = () => {
        if (!currentNoteFolder) return;
        const updatedFolders = folders.map((f: Folder) => 
            f.id === currentNoteFolder.id ? { ...f, noteContent: noteContent } : f
        );
        setFolders(updatedFolders);
        setIsNoteEditorOpen(false);
        setModalConfig({ isOpen: true, type: 'info', title: '儲存成功', message: `已更新「${currentNoteFolder.name}」的講義內容。` });
    };

    const handleGenerateNote = async () => {
        if (!currentNoteFolder) return;
        
        const apiKey = getApiKey();
        if (!apiKey) {
             setModalConfig({ isOpen: true, type: 'error', title: '未設定 API Key', message: '請先設定 Google Gemini API Key 才能使用 AI 生成功能。' });
             return;
        }

        setIsGeneratingAI(true);
        try {
            const ai = new GoogleGenAI({ apiKey });
            const model = 'gemini-2.5-flash';
            
            // Context: Get sub-topics or example questions to help AI
            const sampleQuestions = questions
                .filter((q: Question) => q.folderId === currentNoteFolder.id)
                .slice(0, 5)
                .map((q: Question) => q.text)
                .join("\n");

            const prompt = `
                請為數學單元「${currentNoteFolder.name}」撰寫一份重點講義 (Lecture Note)。
                對象：國小高年級或國中生。
                格式：Markdown。請使用 # ## 標題，以及 - 列點。
                
                內容要求：
                1. 本單元核心觀念與定義。
                2. 重要公式或解題技巧。
                3. 常見錯誤提醒。
                4. 若有以下相關題目內容供參考：${sampleQuestions}。
                
                請直接輸出講義內容，不需多餘的開場白。
            `;
            
            const response = await ai.models.generateContent({
                model: model,
                contents: prompt,
            });
            
            setNoteContent(prev => prev + (prev ? "\n\n" : "") + (response.text || ""));
        } catch (e) {
            setModalConfig({ isOpen: true, type: 'error', title: 'AI 生成失敗', message: '請檢查 API Key 或網路連線。' });
        } finally {
            setIsGeneratingAI(false);
        }
    };

    // --- Move Question Logic ---
    const toggleSelection = (id: number) => {
        const newSet = new Set(selectedQuestionIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedQuestionIds(newSet);
    };

    const handleBulkMoveClick = () => {
        if (selectedQuestionIds.size === 0) return;
        setMoveTargetFolderId(""); // Reset selection
        setIsMoveModalOpen(true);
    };

    const handleSingleMoveClick = (q: Question) => {
        // Only select this one for moving
        setSelectedQuestionIds(new Set([q.id]));
        setMoveTargetFolderId("");
        setIsMoveModalOpen(true);
    };

    const confirmMove = () => {
        if (!moveTargetFolderId) {
            alert("請選擇目標資料夾");
            return;
        }
        
        const updatedQuestions = questions.map((q: Question) => 
            selectedQuestionIds.has(q.id) ? { ...q, folderId: moveTargetFolderId } : q
        );
        
        setQuestions(updatedQuestions);
        setIsMoveModalOpen(false);
        setSelectedQuestionIds(new Set()); // Clear selection after move
        setModalConfig({ isOpen: true, type: 'info', title: '移動成功', message: `已成功移動 ${selectedQuestionIds.size} 筆題目。` });
    };

    const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const val = tagInput.trim();
          if (val) {
            // Append tag to category
            const currentTags = getTags(formData.category);
            if (!currentTags.includes(val)) {
                 const newCategory = formData.category ? `${formData.category}#${val}` : val;
                 setFormData({ ...formData, category: newCategory });
            }
            setTagInput('');
          }
        }
    };

    const handleOpenExportModal = () => {
        const date = new Date().toISOString().split('T')[0];
        setExportFileName(`math-quiz-export-${date}`);
        setIsExportModalOpen(true);
    };
    
    const handleExportConfirm = (format: 'json' | 'csv') => {
        if (format === 'json') {
            const data = {
                questions: questions,
                folders: folders
            };
            downloadFile(JSON.stringify(data, null, 2), `${exportFileName}.json`, 'application/json');
        } else {
            const csvContent = questionsToCSV(questions);
            downloadFile(csvContent, `${exportFileName}.csv`, 'text/csv');
        }
        setIsExportModalOpen(false);
    };

    const handleImportJSON = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = JSON.parse(evt.target?.result as string);
                
                let importCount = 0;
                let updatedCount = 0;
                let newCount = 0;
                let noteUpdateCount = 0;

                if (data.questions && Array.isArray(data.questions)) {
                    // Create a Map of existing questions for O(1) lookup
                    // Use Upsert logic: if ID exists, overwrite it. If not, add it.
                    // This prevents duplication of the same questions when restoring backups.
                    const questionMap = new Map<number, Question>(
                        questions.map((q: Question) => [q.id, q])
                    );

                    data.questions.forEach((q: Question) => {
                        if (questionMap.has(q.id)) {
                            updatedCount++;
                        } else {
                            newCount++;
                        }
                        questionMap.set(q.id, q);
                    });

                    // Convert map back to array and sort by ID
                    const mergedQuestions = Array.from(questionMap.values()).sort((a, b) => a.id - b.id);
                    setQuestions(mergedQuestions);
                    importCount = data.questions.length;
                }
                
                if (data.folders && Array.isArray(data.folders)) {
                    // Smart Merge Logic for Folders
                    const folderMap = new Map<string, Folder>((folders as Folder[]).map((f) => [f.id, f]));
                    
                    (data.folders as any[]).forEach((importedFolder: any) => {
                        const folderData = importedFolder as Folder;
                        const importedNote = folderData.noteContent;

                        if (folderMap.has(folderData.id)) {
                            const existing = folderMap.get(folderData.id)!;
                            const existingNote = existing.noteContent;
                            
                            // Check if note is being updated (only if imported note exists and is different)
                            if (importedNote && importedNote !== existingNote) {
                                noteUpdateCount++;
                            }

                            folderMap.set(folderData.id, {
                                ...existing,
                                name: folderData.name,
                                noteContent: importedNote || existingNote,
                                parentId: folderData.parentId
                            });
                        } else {
                            // New Folder
                            if (importedNote) {
                                noteUpdateCount++;
                            }
                            folderMap.set(folderData.id, folderData);
                        }
                    });
                    
                    setFolders(Array.from(folderMap.values()));
                }
                
                let message = `成功匯入 ${importCount} 筆題目。\n(新增: ${newCount} 筆, 更新: ${updatedCount} 筆)`;
                if (noteUpdateCount > 0) {
                    message += `\n\n此外，已同步更新或新增了 ${noteUpdateCount} 份單元講義。`;
                }
                
                setModalConfig({ 
                    isOpen: true, 
                    type: 'info', 
                    title: '匯入成功', 
                    message: message 
                });
            } catch (err) {
                setModalConfig({ isOpen: true, type: 'error', title: '匯入失敗', message: '檔案格式錯誤' });
            }
        };
        reader.readAsText(file);
        e.target.value = '';
    };
  
    const handleImportCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            const text = evt.target?.result as string;
            const parsed = parseCSV(text);
            let maxId = questions.length > 0 ? Math.max(...questions.map((q:Question) => q.id)) : 0;
            const newQuestions = parsed.map(q => ({
                id: ++maxId,
                folderId: q.folderId || (isSystemRoot ? null : currentFolderId),
                category: q.category || '未分類',
                text: q.text || '',
                options: q.options || ['','','',''],
                correctAnswerIndex: q.correctAnswerIndex || 0,
                explanation: q.explanation || ''
            } as Question));
            
            if (newQuestions.length > 0) {
                setQuestions([...questions, ...newQuestions]);
                setModalConfig({ isOpen: true, type: 'info', title: '匯入成功', message: `成功匯入 ${newQuestions.length} 筆題目` });
            } else {
                 setModalConfig({ isOpen: true, type: 'error', title: '匯入失敗', message: '無法解析 CSV 資料' });
            }
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        setIsRecognizingImage(true);
        
        try {
            const base64 = await new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve(e.target?.result as string);
                reader.readAsDataURL(file);
            });
            
            const base64Data = base64.split(',')[1];
            const mimeType = file.type;
  
            const apiKey = getApiKey();
            if (!apiKey) {
                throw new Error("API Key not found");
            }

            const ai = new GoogleGenAI({ apiKey });
            const modelName = 'gemini-2.5-flash'; 
  
            const prompt = `
              Analyze this image of a math question. Extract the following information and return it in JSON format:
              {
                "text": "The question text",
                "options": ["Option A", "Option B", "Option C", "Option D"],
                "correctAnswerIndex": 0,
                "explanation": "Brief explanation if available, or step-by-step solution"
              }
              If options are not present, leave options as empty strings. 
              Translate to Traditional Chinese if it's not.
            `;
  
            const response = await ai.models.generateContent({
                model: modelName,
                contents: {
                    parts: [
                        { inlineData: { mimeType: mimeType, data: base64Data } },
                        { text: prompt }
                    ]
                }
            });
  
            const text = response.text;
            if (!text) {
                 throw new Error("No text returned from AI");
            }
            const jsonMatch = text.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                const jsonStr = jsonMatch[0];
                const result = JSON.parse(jsonStr);
                
                setFormData({
                    ...emptyForm,
                    text: result.text || '',
                    options: result.options && result.options.length === 4 ? result.options : ['', '', '', ''],
                    correctAnswerIndex: typeof result.correctAnswerIndex === 'number' ? result.correctAnswerIndex : 0,
                    explanation: result.explanation || '',
                    category: formData.category || '未分類'
                });
                setIsAdding(true);
                setEditingId(null);
            } else {
                throw new Error("Invalid response format");
            }
  
        } catch (error) {
            console.error("AI Error:", error);
            setModalConfig({ isOpen: true, type: 'error', title: '辨識失敗', message: '無法辨識圖片內容，請檢查 API Key 設定或網路連線。' });
        } finally {
            setIsRecognizingImage(false);
            e.target.value = '';
        }
    };
    
    // --- CSV Quick Input Logic ---
    const handleQuickCSVSubmit = () => {
        if (!quickCsvContent.trim()) return;

        const lines = quickCsvContent.split(/\r?\n/).filter(l => l.trim());
        
        if (lines.length < 2) {
             setModalConfig({ isOpen: true, type: 'error', title: '格式錯誤', message: '請至少輸入兩行：第一行為標題(會被略過)，第二行開始為資料。' });
             return;
        }

        const dataLines = lines.slice(1);
        const newQuestions: Question[] = [];
        let maxId = questions.length > 0 ? Math.max(...questions.map((q: Question) => q.id)) : 0;
        let parseErrors = 0;

        // Force target folder to be current folder (or null if system root)
        // Ignoring ID and FolderID columns from input if present
        const targetFolderId = isSystemRoot ? null : currentFolderId;

        dataLines.forEach(line => {
            const cols = parseCSVLine(line);
            let q: Partial<Question> = {};
            
            // Format 1: Full Export (10 columns) -> ID, FolderID, Category, Text, Options...
            // We ignore cols[0] (ID) and cols[1] (FolderID)
            if (cols.length >= 10) {
                 q = {
                     folderId: targetFolderId, 
                     category: cols[2],
                     text: cols[3],
                     options: [cols[4], cols[5], cols[6], cols[7]],
                     correctAnswerIndex: parseInt(cols[8], 10) || 0,
                     explanation: cols[9]
                 };
            } 
            // Format 2: Simplified (8 columns) -> Category, Text, Options...
            else if (cols.length >= 8) {
                 q = {
                     folderId: targetFolderId, 
                     category: cols[0],
                     text: cols[1],
                     options: [cols[2], cols[3], cols[4], cols[5]],
                     correctAnswerIndex: parseInt(cols[6], 10) || 0,
                     explanation: cols[7]
                 };
            } else {
                parseErrors++;
                return;
            }

            if (q.text) {
                newQuestions.push({
                    id: ++maxId,
                    folderId: q.folderId, 
                    category: q.category || '未分類',
                    text: q.text,
                    options: q.options || ['','','',''],
                    correctAnswerIndex: q.correctAnswerIndex || 0,
                    explanation: q.explanation || ''
                });
            }
        });

        if (newQuestions.length > 0) {
             setQuestions([...questions, ...newQuestions]);
             setIsCsvQuickAddOpen(false);
             setQuickCsvContent("");
             setIsFabOpen(false);
             setModalConfig({ 
                 isOpen: true, 
                 type: 'info', 
                 title: '匯入成功', 
                 message: `已成功新增 ${newQuestions.length} 筆題目至當前資料夾！${parseErrors > 0 ? ` (略過 ${parseErrors} 筆格式錯誤)` : ''}` 
             });
        } else {
             setModalConfig({ isOpen: true, type: 'error', title: '解析失敗', message: '無法識別有效題目資料，請檢查欄位數量與格式。' });
        }
    };

    const handleGenerateExplanation = async () => {
        if (!formData.text) return;
        
        const apiKey = getApiKey();
        if (!apiKey) {
             setModalConfig({ isOpen: true, type: 'error', title: '未設定 API Key', message: '請先至「系統設定」頁面設定 Google Gemini API Key，或確認環境變數。' });
             return;
        }

        setIsGeneratingAI(true);
        try {
            const ai = new GoogleGenAI({ apiKey });
            const model = 'gemini-2.5-flash';
            const prompt = `
                請為以下數學題目產生詳細的解析 (Explanation)：
                題目：${formData.text}
                選項：${formData.options.join(', ')}
                
                解析請包含解題步驟與觀念說明，並以繁體中文回答。
            `;
            const response = await ai.models.generateContent({
                model: model,
                contents: prompt,
            });
            setFormData(prev => ({ ...prev, explanation: response.text || '' }));
        } catch (e) {
            setModalConfig({ isOpen: true, type: 'error', title: 'AI 生成失敗', message: '請檢查 API Key 設定是否正確或網路連線。' });
        } finally {
            setIsGeneratingAI(false);
        }
    };

    const openCreateFolderModal = () => {
        setFolderNameInput("");
        setModalConfig({
            isOpen: true,
            type: 'create-folder',
            title: isSystemRoot ? '建立新年級/題庫' : '建立新資料夾'
        });
    };

    const openRenameFolderModal = (folder: Folder) => {
        setFolderNameInput(folder.name);
        setModalConfig({
            isOpen: true,
            type: 'rename-folder',
            targetId: folder.id,
            title: isSystemRoot ? '修改年級名稱' : '重新命名資料夾'
        });
    };

    const openDeleteFolderModal = (id: string) => {
        setModalConfig({
            isOpen: true,
            type: 'delete-folder',
            targetId: id,
            title: isSystemRoot ? '刪除整個年級/題庫' : '刪除資料夾'
        });
    };

    const openDeleteQuestionModal = (id: number) => {
        setModalConfig({
            isOpen: true,
            type: 'delete-question',
            targetId: id,
            title: '刪除題目'
        });
    };

    const handleConfirmModal = () => {
        const { type, targetId } = modalConfig;

        if (type === 'create-folder') {
            if (folderNameInput.trim()) {
                const newFolder: Folder = {
                    id: 'folder-' + Date.now(),
                    name: folderNameInput.trim(),
                    parentId: isSystemRoot ? null : currentFolderId
                };
                setFolders([...folders, newFolder]);
            }
        } else if (type === 'rename-folder' && typeof targetId === 'string') {
             if (folderNameInput.trim()) {
                 setFolders(folders.map((f: Folder) => 
                     f.id === targetId ? { ...f, name: folderNameInput.trim() } : f
                 ));
             }
        } else if (type === 'delete-folder' && typeof targetId === 'string') {
            const idsToDelete = new Set<string>();
            const collectIds = (fid: string) => {
                idsToDelete.add(fid);
                const children = folders.filter((f: Folder) => f.parentId === fid);
                children.forEach((c: Folder) => collectIds(c.id));
            };
            collectIds(targetId);

            setFolders(folders.filter((f: Folder) => !idsToDelete.has(f.id)));
            setQuestions(questions.filter((q: Question) => !q.folderId || !idsToDelete.has(q.folderId)));
        } else if (type === 'delete-question' && typeof targetId === 'number') {
            setQuestions(questions.filter((q: Question) => q.id !== targetId));
        }

        setModalConfig({ ...modalConfig, isOpen: false });
    };

    const getModalContent = () => {
        switch (modalConfig.type) {
            case 'create-folder':
            case 'rename-folder':
                return (
                    <div>
                        <label className="block text-gray-700 font-bold mb-2">
                            {isSystemRoot && modalConfig.type === 'create-folder' ? '年級/題庫名稱' : '資料夾名稱'}
                        </label>
                        <input
                            type="text"
                            value={folderNameInput}
                            onChange={(e) => setFolderNameInput(e.target.value)}
                            className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                            placeholder="請輸入名稱"
                            autoFocus
                        />
                    </div>
                );
            case 'delete-folder':
                return (
                    <div className="flex items-center text-gray-700">
                         <AlertTriangle className="w-10 h-10 text-red-500 mr-4 shrink-0" />
                         <div>
                             <p className="font-bold text-lg">確定要刪除此{isSystemRoot ? '年級' : '資料夾'}嗎？</p>
                             <p className="text-sm mt-1">
                                 {isSystemRoot 
                                    ? '警告：將會刪除該年級下的「所有科目」、「題目」與「學習紀錄」，此動作無法復原！'
                                    : '資料夾內的「所有題目」與「子資料夾」都將被永久刪除！'}
                             </p>
                         </div>
                    </div>
                );
            case 'delete-question':
                return (
                    <div className="flex items-center text-gray-700">
                         <Trash2 className="w-10 h-10 text-red-500 mr-4" />
                         <div>
                             <p className="font-bold text-lg">確定要刪除此題目嗎？</p>
                             <p className="text-sm">此動作無法復原。</p>
                         </div>
                    </div>
                );
            case 'info':
                 return (
                     <div className="flex items-center text-gray-700">
                         <CheckCircle className="w-10 h-10 text-green-500 mr-4" />
                         <p className="text-lg whitespace-pre-line">{modalConfig.message}</p>
                     </div>
                 );
            case 'error':
                 return (
                     <div className="flex items-center text-gray-700">
                         <XCircle className="w-10 h-10 text-red-500 mr-4" />
                         <p className="text-lg">{modalConfig.message}</p>
                     </div>
                 );
            default:
                return null;
        }
    };
    
    return (
        <div className="flex-1 bg-gray-100 p-4 md:p-8 overflow-y-auto relative">
            <Modal
                isOpen={modalConfig.isOpen}
                title={modalConfig.title}
                onCancel={() => setModalConfig({ ...modalConfig, isOpen: false })}
                onConfirm={handleConfirmModal}
                isDanger={!!modalConfig.type?.startsWith('delete')}
                confirmText={modalConfig.type?.startsWith('delete') ? '確認刪除' : (modalConfig.type === 'rename-folder' ? '儲存' : '建立')}
                showConfirm={modalConfig.type !== 'info' && modalConfig.type !== 'error'}
                cancelText={modalConfig.type !== 'info' && modalConfig.type !== 'error' ? '取消' : '關閉'}
            >
                {getModalContent()}
            </Modal>

            {/* Move Question Modal (Bulk) */}
            <Modal
                isOpen={isMoveModalOpen}
                title={`移動 ${selectedQuestionIds.size} 筆題目至...`}
                onCancel={() => setIsMoveModalOpen(false)}
                onConfirm={confirmMove}
                confirmText="確認移動"
            >
                <div className="space-y-4">
                    <div>
                        <p className="text-sm text-gray-500 mb-1">
                            請選擇目標資料夾。移動後，題目將從目前資料夾移除。
                        </p>
                        <p className="text-xs text-blue-600 font-bold bg-blue-50 inline-block px-2 py-1 rounded">
                            移動範圍限制於：{scopeName}
                        </p>
                    </div>
                    <div className="relative">
                        <FolderIcon className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <select
                            className="w-full pl-10 p-3 border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none appearance-none"
                            value={moveTargetFolderId}
                            onChange={(e) => setMoveTargetFolderId(e.target.value)}
                        >
                            <option value="" disabled>請選擇資料夾...</option>
                            {availableTargetFolders.map((f: Folder) => (
                                    <option key={f.id} value={f.id}>
                                        {getFolderPathString(f)}
                                    </option>
                                ))
                            }
                        </select>
                        <ChevronRight className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none rotate-90" />
                    </div>
                    {availableTargetFolders.length === 0 && (
                        <p className="text-center text-red-400 text-sm">在此範圍內沒有其他可用的資料夾</p>
                    )}
                </div>
            </Modal>

            {/* Note Editor Modal */}
            <Modal
                isOpen={isNoteEditorOpen}
                title={`編輯講義 - ${currentNoteFolder?.name}`}
                onCancel={() => setIsNoteEditorOpen(false)}
                onConfirm={saveNote}
                confirmText="儲存講義"
            >
                <div className="flex flex-col h-[60vh] space-y-2">
                    <div className="flex justify-between items-center bg-gray-50 p-2 rounded text-sm text-gray-500">
                        <span>支援 Markdown 語法 ( # 標題, - 列表, **粗體** )</span>
                        <button 
                            onClick={handleGenerateNote}
                            disabled={isGeneratingAI}
                            className="flex items-center text-fudan-blue hover:bg-blue-100 px-2 py-1 rounded transition-colors disabled:opacity-50"
                        >
                            <Sparkles className="w-4 h-4 mr-1" />
                            AI 自動生成
                        </button>
                    </div>
                    <textarea 
                        value={noteContent}
                        onChange={(e) => setNoteContent(e.target.value)}
                        className="flex-1 border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm leading-relaxed resize-none"
                        placeholder="在此輸入講義內容..."
                    />
                </div>
            </Modal>

            {/* Export Modal */}
            <Modal
                isOpen={isExportModalOpen}
                title="匯出資料"
                onCancel={() => setIsExportModalOpen(false)}
                showConfirm={false}
                cancelText="關閉"
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold mb-1">檔案名稱</label>
                        <input 
                            type="text" 
                            value={exportFileName} 
                            onChange={(e) => setExportFileName(e.target.value)}
                            className="w-full border p-2 rounded"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <button onClick={() => handleExportConfirm('json')} className="flex flex-col items-center justify-center p-4 border rounded hover:bg-gray-50">
                            <Database className="w-8 h-8 text-blue-500 mb-2"/>
                            <span className="font-bold">JSON 格式</span>
                            <span className="text-xs text-gray-500">包含資料夾結構與講義，完整備份</span>
                        </button>
                        <button onClick={() => handleExportConfirm('csv')} className="flex flex-col items-center justify-center p-4 border rounded hover:bg-gray-50">
                            <Sheet className="w-8 h-8 text-green-500 mb-2"/>
                            <span className="font-bold">CSV 格式</span>
                            <span className="text-xs text-gray-500">僅題目資料，方便 Excel 編輯</span>
                        </button>
                    </div>
                </div>
            </Modal>

            {/* CSV Quick Add Modal */}
            <Modal
                 isOpen={isCsvQuickAddOpen}
                 title="快速輸入 (CSV)"
                 onCancel={() => setIsCsvQuickAddOpen(false)}
                 onConfirm={handleQuickCSVSubmit}
                 confirmText="匯入"
            >
                 <div className="space-y-2">
                     <p className="text-sm text-gray-500">
                         請貼上 CSV 格式的題目資料。
                         <br />
                         格式 1 (忽略ID)：<code className="bg-gray-100 px-1">(ID, FolderID), 分類, 題目, 選項A, 選項B, 選項C, 選項D, 答案(0-3), 詳解</code>
                         <br />
                         格式 2 (簡化)：<code className="bg-gray-100 px-1">分類, 題目, 選項A, 選項B, 選項C, 選項D, 答案(0-3), 詳解</code>
                         <br />
                         <span className="text-red-500 text-xs">* 所有題目將直接匯入至「目前所在的資料夾」。</span>
                     </p>
                     <textarea
                         value={quickCsvContent}
                         onChange={(e) => setQuickCsvContent(e.target.value)}
                         className="w-full h-48 border p-2 rounded text-sm font-mono"
                         placeholder="分類,題目..."
                     />
                 </div>
            </Modal>
            
            <input type="file" ref={jsonInputRef} onChange={handleImportJSON} accept=".json" className="hidden" />
            <input type="file" ref={csvInputRef} onChange={handleImportCSV} accept=".csv" className="hidden" />
            <input type="file" ref={imageInputRef} onChange={handleImageSelect} accept="image/*" className="hidden" />

            {/* AI Processing Overlay */}
            {(isRecognizingImage || isGeneratingAI) && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm">
                    <div className="bg-white p-6 rounded-2xl shadow-2xl flex flex-col items-center animate-bounce-in">
                        <Loader2 className="w-10 h-10 text-fudan-blue animate-spin mb-4" />
                        <h3 className="text-lg font-bold text-gray-800">AI 正在思考中...</h3>
                        <p className="text-sm text-gray-500">請稍候，正在分析內容</p>
                    </div>
                </div>
            )}

            <div className="max-w-6xl mx-auto pb-24">
                <h1 className="text-3xl font-bold text-gray-800 mb-6">題庫資源管理</h1>
                
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center text-gray-600 overflow-x-auto flex-1 min-w-[200px]">
                        <button 
                            onClick={() => setCurrentFolderId(SYSTEM_ROOT_ID)}
                            className={`flex items-center px-2 py-1 hover:bg-gray-100 rounded mr-1 ${isSystemRoot ? 'bg-blue-100 text-blue-700 font-bold' : ''}`}
                            title="管理所有年級"
                        >
                            <Globe className="w-4 h-4 mr-1" />
                            系統根目錄
                        </button>
                        
                        {!isSystemRoot && (
                            <>
                                <ChevronRight className="w-4 h-4 mx-1 text-gray-400 shrink-0" />
                                <button 
                                    onClick={() => setCurrentFolderId(breadcrumbRootId)}
                                    className={`flex items-center hover:text-fudan-blue whitespace-nowrap ${currentFolderId === breadcrumbRootId ? 'font-bold text-fudan-blue' : ''}`}
                                >
                                    <Home className="w-4 h-4 mr-1" />
                                    {breadcrumbRootName}
                                </button>
                                {displayBreadcrumbs.map((crumb) => (
                                    <React.Fragment key={crumb.id}>
                                        <ChevronRight className="w-4 h-4 mx-1 text-gray-400 shrink-0" />
                                        <button 
                                            onClick={() => setCurrentFolderId(crumb.id)}
                                            className={`hover:text-fudan-blue whitespace-nowrap ${currentFolderId === crumb.id ? 'font-bold text-fudan-blue' : ''}`}
                                        >
                                            {crumb.name}
                                        </button>
                                    </React.Fragment>
                                ))}
                            </>
                        )}
                    </div>

                    <div className="flex items-center space-x-2 shrink-0">
                        {/* Sort Mode Toggle */}
                        {currentSubFolders.length > 0 && (
                             <button
                                 onClick={() => setIsReordering(!isReordering)}
                                 className={`px-3 py-2 rounded-lg flex items-center text-sm font-bold transition-colors ${
                                     isReordering 
                                     ? 'bg-orange-100 text-orange-700 border border-orange-200' 
                                     : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                 }`}
                                 title={isReordering ? "完成排序" : "調整資料夾順序"}
                             >
                                 <GripHorizontal className="w-4 h-4 md:mr-1" />
                                 <span className="hidden md:inline">{isReordering ? '完成排序' : '調整順序'}</span>
                             </button>
                        )}

                        {!isSystemRoot && (
                            <div className="relative hidden md:block">
                                <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                                <input 
                                    type="text"
                                    placeholder="搜尋題目..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    className="pl-8 pr-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:ring-fudan-blue focus:border-fudan-blue outline-none w-48"
                                />
                            </div>
                        )}
                        <button onClick={openCreateFolderModal} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center text-sm font-bold">
                            <Plus className="w-4 h-4 md:mr-1" /> <span className="hidden md:inline">{isSystemRoot ? '新增年級' : '資料夾'}</span>
                        </button>
                        
                        {!isSystemRoot && (
                            <>
                                {selectedQuestionIds.size > 0 && (
                                    <button 
                                        onClick={handleBulkMoveClick}
                                        className="px-3 py-2 bg-purple-100 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-200 flex items-center text-sm font-bold transition-colors animate-fade-in"
                                    >
                                        <FolderInput className="w-4 h-4 md:mr-1" /> 
                                        <span className="hidden md:inline">移動 ({selectedQuestionIds.size})</span>
                                    </button>
                                )}
                                <button onClick={startAdd} className="px-3 py-2 bg-fudan-blue text-white rounded-lg hover:bg-blue-800 flex items-center text-sm font-bold shadow">
                                    <Plus className="w-4 h-4 md:mr-1" /> <span className="hidden md:inline">題目</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Edit Modal (Question) */}
                {(isAdding || editingId !== null) && (
                     <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
                        <div className="bg-white w-full md:max-w-4xl max-h-[90vh] h-[90vh] md:h-auto overflow-y-auto rounded-t-2xl md:rounded-2xl shadow-2xl border border-gray-200 flex flex-col relative animate-fade-in-up">
                            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-2xl sticky top-0 z-10">
                                <h3 className="font-bold text-lg text-gray-800">{isAdding ? '新增題目' : '編輯題目'}</h3>
                                <button onClick={() => { setIsAdding(false); setEditingId(null); }} className="text-gray-400 hover:text-gray-600">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="md:col-span-2 space-y-4">
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-1">題目敘述 (支援 LaTeX: $...$)</label>
                                            <div className="relative">
                                                <textarea
                                                    value={formData.text}
                                                    onChange={(e) => setFormData({...formData, text: e.target.value})}
                                                    className="w-full border border-gray-300 p-3 rounded-lg h-32 focus:ring-2 focus:ring-blue-500 outline-none font-serif text-lg"
                                                    placeholder="請輸入題目內容..."
                                                />
                                                <button 
                                                    onClick={() => imageInputRef.current?.click()}
                                                    className="absolute bottom-3 right-3 p-2 bg-gray-100 hover:bg-blue-100 text-gray-500 hover:text-blue-600 rounded-full transition-colors"
                                                    title="從圖片辨識題目 (AI)"
                                                >
                                                    <Camera className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-1">分類標籤 (Enter 新增)</label>
                                            <input
                                                type="text"
                                                value={tagInput}
                                                onChange={(e) => setTagInput(e.target.value)}
                                                onKeyDown={handleTagInputKeyDown}
                                                className="w-full border border-gray-300 p-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                                placeholder="輸入後按 Enter"
                                            />
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {getTags(formData.category).map((tag, idx) => (
                                                    <span key={idx} className="bg-blue-100 text-fudan-blue px-2 py-1 rounded text-sm flex items-center">
                                                        {tag}
                                                        <button 
                                                            onClick={() => {
                                                                const newTags = getTags(formData.category).filter(t => t !== tag);
                                                                setFormData({...formData, category: newTags.join('#')});
                                                            }}
                                                            className="ml-1 hover:text-red-500"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-1">正確答案</label>
                                            <div className="flex space-x-2">
                                                {[0, 1, 2, 3].map((optIdx) => (
                                                    <button
                                                        key={optIdx}
                                                        onClick={() => setFormData({...formData, correctAnswerIndex: optIdx})}
                                                        className={`flex-1 py-2 rounded-lg font-bold border ${
                                                            formData.correctAnswerIndex === optIdx
                                                                ? 'bg-fudan-blue text-white border-fudan-blue'
                                                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {String.fromCharCode(65 + optIdx)}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="block text-sm font-bold text-gray-700">選項內容</label>
                                        {formData.options.map((opt, idx) => (
                                            <div key={idx} className="flex items-center">
                                                <span className="w-8 text-center font-bold text-gray-500">{String.fromCharCode(65 + idx)}</span>
                                                <input
                                                    type="text"
                                                    value={opt}
                                                    onChange={(e) => {
                                                        const newOptions = [...formData.options];
                                                        newOptions[idx] = e.target.value;
                                                        setFormData({...formData, options: newOptions});
                                                    }}
                                                    className="flex-1 border border-gray-300 p-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                                    placeholder={`選項 ${String.fromCharCode(65 + idx)}`}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    <div className="md:col-span-2">
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="block text-sm font-bold text-gray-700">詳解</label>
                                            <button 
                                                onClick={handleGenerateExplanation}
                                                className="text-xs flex items-center text-fudan-blue hover:underline"
                                                disabled={isGeneratingAI}
                                            >
                                                <Sparkles className="w-3 h-3 mr-1" />
                                                AI 自動生成詳解
                                            </button>
                                        </div>
                                        <textarea
                                            value={formData.explanation}
                                            onChange={(e) => setFormData({...formData, explanation: e.target.value})}
                                            className="w-full border border-gray-300 p-3 rounded-lg h-24 focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder="請輸入詳解..."
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end space-x-2 p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl sticky bottom-0">
                                <button onClick={() => { setIsAdding(false); setEditingId(null); }} className="px-4 py-2 text-gray-600 font-bold hover:bg-gray-200 rounded-lg">取消</button>
                                <button onClick={saveForm} className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg shadow-md">儲存</button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-4 mb-8">
                     {/* Show "All Questions" virtual folder only if NOT in System Root */}
                     {!isSystemRoot && !isActiveGradeRoot && currentFolderId && (
                        <div 
                            onClick={() => setShowRecursive(true)}
                            className={`p-4 rounded-lg shadow-sm border flex flex-col justify-between cursor-pointer relative transition-all group mb-4
                                ${showRecursive 
                                    ? 'bg-indigo-100 border-indigo-500 ring-2 ring-indigo-200' 
                                    : 'bg-indigo-50 border-indigo-200 hover:border-indigo-400'
                                }
                            `}
                        >
                            <div className={`flex items-center mb-2 ${showRecursive ? 'text-indigo-700' : 'text-indigo-400'}`}>
                                <Layers className="w-8 h-8" />
                            </div>
                            <div>
                                <span className={`font-bold truncate block ${showRecursive ? 'text-indigo-900' : 'text-indigo-700'}`}>
                                    全部題目
                                </span>
                                <span className="text-xs text-indigo-500 font-medium">
                                    (含子資料夾)
                                </span>
                            </div>
                             {showRecursive && (
                                <div className="absolute top-2 right-2">
                                    <CheckCircle className="w-5 h-5 text-indigo-600" />
                                </div>
                            )}
                        </div>
                    )}

                    {(currentSubFolders.length > 0 || !isActiveGradeRoot || isSystemRoot) && (
                         <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            {currentSubFolders.map((folder: Folder, idx: number) => {
                                const hasNote = folder.noteContent && folder.noteContent.trim().length > 0;
                                return (
                                    <div key={folder.id} 
                                         className={`p-4 rounded-lg shadow-sm border flex flex-col justify-between group cursor-pointer relative transition-all
                                             ${isReordering 
                                                ? 'border-orange-300 ring-1 ring-orange-200 bg-orange-50' 
                                                : (hasNote 
                                                    ? 'bg-amber-50 border-amber-200 hover:border-amber-400 hover:shadow-md' 
                                                    : 'bg-white border-gray-200 hover:border-blue-400')
                                             }
                                         `}
                                         onClick={() => !isReordering && setCurrentFolderId(folder.id)}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <div className={`${hasNote ? 'text-amber-500' : 'text-yellow-500'}`}>
                                                <FolderIcon className="w-8 h-8 fill-current" />
                                            </div>
                                            
                                            {hasNote && !isReordering && (
                                                <div className="bg-white/80 p-1 rounded-full shadow-sm text-amber-600" title="包含重點講義">
                                                    <BookOpen className="w-4 h-4" />
                                                </div>
                                            )}
                                            
                                            {/* Reorder Buttons */}
                                            {isReordering && (
                                                <div className="flex bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); moveFolder(folder.id, 'left'); }}
                                                        disabled={idx === 0}
                                                        className="p-1.5 hover:bg-orange-100 text-gray-500 disabled:opacity-30 disabled:hover:bg-transparent"
                                                    >
                                                        <ArrowLeft className="w-4 h-4" />
                                                    </button>
                                                    <div className="w-px bg-gray-200"></div>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); moveFolder(folder.id, 'right'); }}
                                                        disabled={idx === currentSubFolders.length - 1}
                                                        className="p-1.5 hover:bg-orange-100 text-gray-500 disabled:opacity-30 disabled:hover:bg-transparent"
                                                    >
                                                        <ArrowRight className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <span className={`font-bold truncate block h-6 ${hasNote ? 'text-amber-900' : 'text-gray-700'}`}>{folder.name}</span>
                                        
                                        {!isReordering && (
                                            <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {/* Note Edit Button */}
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); openNoteEditor(folder); }}
                                                    className={`p-1 hover:bg-yellow-100 rounded ${folder.noteContent ? 'text-yellow-600' : 'text-gray-300 hover:text-yellow-600'}`}
                                                    title={folder.noteContent ? "編輯講義 (已有內容)" : "撰寫講義"}
                                                >
                                                    <FileEdit className="w-4 h-4" />
                                                </button>
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); openRenameFolderModal(folder); }}
                                                    className="p-1 text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded"
                                                    title="重新命名"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); openDeleteFolderModal(folder.id); }}
                                                    className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded"
                                                    title="刪除"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                         </div>
                    )}

                    {!isSystemRoot && (
                        <div className="grid gap-3">
                            {/* ... (Existing Questions List) ... */}
                            {filteredQuestions.length === 0 && currentSubFolders.length === 0 && (
                                <div className="text-center py-12 text-gray-400 bg-white rounded-xl border-dashed border-2 border-gray-200">
                                    找不到資料，請新增。
                                </div>
                            )}
                            {filteredQuestions.map((q: Question) => {
                                 const isSelected = selectedQuestionIds.has(q.id);
                                 return (
                                     <div key={q.id} className={`bg-white p-4 rounded-lg shadow-sm border flex flex-col md:flex-row justify-between items-start md:items-center group transition-colors ${isSelected ? 'border-purple-300 ring-1 ring-purple-200 bg-purple-50' : 'border-gray-200 hover:border-blue-300'}`}>
                                        <div className="flex-1 pr-4 flex items-start">
                                            <button 
                                                onClick={() => toggleSelection(q.id)}
                                                className="mr-3 mt-1 text-gray-400 hover:text-purple-600 focus:outline-none"
                                            >
                                                {isSelected ? <CheckSquare className="w-6 h-6 text-purple-600" /> : <Square className="w-6 h-6" />}
                                            </button>
                                            <div>
                                                <div className="flex flex-wrap gap-1 mb-2">
                                                    {getTags(q.category).map((tag, idx) => (
                                                        <span key={idx} className="bg-blue-100 text-fudan-blue text-xs font-bold px-2 py-1 rounded">{tag}</span>
                                                    ))}
                                                </div>
                                                <h4 className="font-bold text-gray-800 line-clamp-2">{q.text}</h4>
                                            </div>
                                        </div>
                                        <div className="flex items-center mt-4 md:mt-0 space-x-2 shrink-0 ml-9 md:ml-0">
                                            <button onClick={() => handleSingleMoveClick(q)} className="p-2 text-purple-600 hover:bg-purple-50 rounded-full" title="移動至其他資料夾">
                                                <FolderInput className="w-5 h-5" />
                                            </button>
                                            <button onClick={() => startEdit(q)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-full" title="編輯">
                                                <Edit className="w-5 h-5" />
                                            </button>
                                            <button onClick={() => openDeleteQuestionModal(q.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-full" title="刪除">
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                 );
                            })}
                        </div>
                    )}
                </div>

                <div className="border-t border-gray-200 pt-8 mt-8">
                    {/* ... (Existing Backup Section) ... */}
                    <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                        <Database className="w-5 h-5 mr-2" /> 資料備份
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col items-center text-center">
                            <Download className="w-10 h-10 text-gray-400 mb-4" />
                            <h3 className="font-bold text-gray-800 mb-2">匯出資料</h3>
                            <p className="text-sm text-gray-500 mb-4">將所有題目與設定匯出為檔案</p>
                            <button 
                                onClick={handleOpenExportModal}
                                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2 rounded-lg transition-colors"
                            >
                                匯出
                            </button>
                        </div>
                        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col items-center text-center">
                            <Upload className="w-10 h-10 text-gray-400 mb-4" />
                            <h3 className="font-bold text-gray-800 mb-2">匯入備份</h3>
                            <p className="text-sm text-gray-500 mb-4">從 JSON 檔案還原資料</p>
                            <button 
                                onClick={() => jsonInputRef.current?.click()}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 rounded-lg transition-colors"
                            >
                                選擇檔案還原
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end space-y-3">
                {isFabOpen && !isSystemRoot && (
                    <>
                        <button 
                            className="flex items-center px-4 py-2 bg-white rounded-full shadow-lg text-gray-700 hover:bg-gray-50 transition-all transform hover:scale-105"
                            onClick={() => imageInputRef.current?.click()}
                        >
                            <Camera className="w-5 h-5 mr-2 text-fudan-blue" />
                            拍照匯入 (AI)
                        </button>
                        <button 
                            className="flex items-center px-4 py-2 bg-white rounded-full shadow-lg text-gray-700 hover:bg-gray-50 transition-all transform hover:scale-105"
                            onClick={() => { setIsCsvQuickAddOpen(true); setIsFabOpen(false); }}
                        >
                            <Keyboard className="w-5 h-5 mr-2 text-green-600" />
                            文字輸入 (CSV)
                        </button>
                    </>
                )}
                {!isSystemRoot && (
                    <button 
                        onClick={() => setIsFabOpen(!isFabOpen)}
                        className={`w-14 h-14 bg-fudan-blue rounded-full shadow-2xl flex items-center justify-center text-white transition-transform ${isFabOpen ? 'rotate-45' : 'hover:scale-110'}`}
                    >
                        <Plus className="w-8 h-8" />
                    </button>
                )}
            </div>
        </div>
    );
};
